import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { CommonUtils } from '../../shared/utils/common-utils';
import { globals } from '../../app.globals';
import { GlobalFiltersService } from '../../shared/services/common/global-filters.service';
import { SideBarService } from '../../shared/services/common/side-bar.service';
import { DateUtils } from '../../shared/utils/date-utils';
import { GetOutletArrPipe } from '../../shared/pipes/get-outlet-arr.pipe';

interface Year {
  Label: string;
  Id: string;
  Value: number;
}

@Component({
  selector: 'app-filter-layout',
  templateUrl: './filter-layout.component.html',
  styleUrls: ['./filter-layout.component.scss'],
})
export class FilterLayoutComponent implements OnInit, OnDestroy {
  visible: boolean = false;
  fetchHeirarchySubscription: Subscription;
  additionalControlSubscription: Subscription;
  routeSubscription: Subscription;

  get formData() {
    return this.globalFilterForm.controls;
  }
  @Output() filterChanged = new EventEmitter<any>();
  globalFilterForm!: FormGroup;
  maxDate: any;
  public reportTypeOption;
  public sTypeOption;
  private monthDefaultVal;
  public rTypeOption;
  private commonUtils = new CommonUtils();
  public yearArr: Year[] = this.commonUtils.getPreviosYears(5);
  public monthArr;
  public quarterArr = globals.quarterOptions;
  public additionalFields;
  hierarchyData: any = {};
  dataLoaded = false;
  public rangeElements = [];
  rangeOutput = {};

  constructor(
    private formBuilder: FormBuilder,
    private globalFilterService: GlobalFiltersService,
    //private datePipe: DatePipe,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sidebarService: SideBarService
  ) { }

  ngOnInit() {
    this.sidebarService.sidebarVisibility$.subscribe(
      (isVisible) => (this.visible = isVisible)
    );
    const dateUtils = new DateUtils();
    this.initializeForm();
    this.executeHierarchyWithoutAdditionalFilters();
    this.routeSubscription = this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.initializeForm();
        this.executeHierarchyWithoutAdditionalFilters();
        this.rangeElements = [];
      }
    });
    this.globalFilterForm = this.formBuilder.group({
      Region: undefined,
      SubRegion: undefined,
      Cluster: undefined,
      City: undefined,
      Outlet: undefined,
      Session: '',
      ToDate: dateUtils.getNow(-1),
      FromDate: '',
      Year: '',
      Quarter: '',
      Month: '',
      RType: '',
      SType: '',
      SSG: '',
      TillDate: '',
    });
    this.onApplyFilters();
    this.addFormDataChangeListeners();
    this.additionalControlSubscription = this.globalFilterService.onControlChange().subscribe(data => {
      //console.log(data.visible)
      if (data.visible) {
        this.additionalFields[data.fieldName].visible = !!data.show;
        switch (data.fieldName) {
          case 'yearPicker':
            //this.globalFilterForm.controls['Year'].setValue(data.defaultVal);
            const _year = this.yearArr.filter(ans => ans.Value === data.defaultVal)[0];
            this.globalFilterForm.controls['Year'].setValue(_year);
            break;
          case 'quarterPicker':
            this.globalFilterForm.controls['Quarter'].setValue(data.defaultVal);
            break;
          case 'monthPicker':
            this.monthDefaultVal = data.defaultVal;
            this.monthArr = this.commonUtils.getMonthByLimit(data.defaultVal);
            const _month = this.monthArr.filter(ans => ans.Value === data.defaultVal.toString())[0];
            this.globalFilterForm.controls['Month'].setValue(_month);
            // this.globalFilterForm.controls['Month'].setValue(data.defaultVal);
            break;
          case 'toDatePicker':
            const toDate = this.formatDateForSafari(data.defaultVal);
            this.globalFilterForm.controls['ToDate'].setValue(toDate);
            this.maxDate = data.maxDate ? toDate : {};
            break;
          case 'fromDatePicker':
            const fromDate = this.formatDateForSafari(data.defaultVal);
            this.globalFilterForm.controls['FromDate'].setValue(fromDate);
            break;
          case 'sTypeFilter':
            this.sTypeOption = data.option;
            if (data.defaultVal >= 0) {
              this.globalFilterForm.controls['SType'].setValue(
                this.sTypeOption[data.defaultVal],
              );
            }
            break;
          case 'rTypeFilter':
            this.rTypeOption = data.option;
            if (data.defaultVal >= 0) {
              this.globalFilterForm.controls['RType'].setValue(
                this.rTypeOption[data.defaultVal],
              );
            }
            break;
          case 'SSGFilter':
            if (data.defaultVal) {
              this.globalFilterForm.controls['SSG'].setValue(data.defaultVal);
            }
            break;
          case 'tillDatePicker':
            const tillDate = this.formatDateForSafari(data.defaultVal);
            this.globalFilterForm.controls['TillDate'].setValue(tillDate);
            break;
        }
      } else {
        this.additionalFields[data.fieldName].visible = false;
      }
      this.globalFilterService.setData(this.getFilterDataObject());
    });

    this.globalFilterService.onHierarchyStoreFilterChange().subscribe((res: any) => {

      this.fetchHierarchyData('', '', '', res.typeOfUnit, res.activeOnly);
    })
  }

  private formatDateForSafari(val: { year: number, month: number, day: number }): Date {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return new Date(`${val.year}-${pad(val.month)}-${pad(val.day)}`);
  }

  initializeForm() {
    this.additionalFields = {
      yearPicker: {
        visible: false,
      },
      quarterPicker: {
        visible: false,
      },
      monthPicker: {
        visible: false,
      },
      toDatePicker: {
        visible: false,
      },
      fromDatePicker: {
        visible: false,
      },
      sessionFilter: {
        visible: false,
      },
      rTypeFilter: {
        visible: false,
      },
      sTypeFilter: {
        visible: false,
      },
      SSGFilter: {
        visible: false,
      },
      hierarchyControls: {
        visible: true,
      },
      tillDatePicker: {
        visible: false,
      },
    };
  }

  addFormDataChangeListeners() {
    this.globalFilterForm.get('Month')?.valueChanges.subscribe(val => {
      if (this.globalFilterForm.controls['Month'].value) {
        const currDate = this.commonUtils.getFirstDayOfMonth(
          this.globalFilterForm.controls['Year'].value?.Value, this.globalFilterForm.controls['Month'].value?.Value);
        const currDate2 = this.commonUtils.getLastDayOfMonth(
          this.globalFilterForm.controls['Year'].value?.Value, this.globalFilterForm.controls['Month'].value?.Value);
        this.fetchHierarchyData(this.globalFilterForm.controls['SSG'].value, currDate, currDate2);
      }
    });
    this.globalFilterForm.get('ToDate')?.valueChanges.subscribe(val => {
      if (this.globalFilterForm.controls['ToDate'].value) {
        const dt = moment(new Date((this.globalFilterForm.controls['ToDate'].value))).format("yyyy-MM-DD");
        const currDate = this.commonUtils.getFirstDayOfMonthByDate(dt);
        this.fetchHierarchyData(this.globalFilterForm.controls['SSG'].value, currDate, dt);
      }
    });
    this.globalFilterForm.get('TillDate')?.valueChanges.subscribe(val => {
      if (this.globalFilterForm.controls['TillDate'].value) {
        const dt = moment(new Date((this.globalFilterForm.controls['TillDate'].value))).format("yyyy-MM-DD");
        // const dt = this.datePipe.transform(
        //   new Date(DateUtils.getDateStrByObj(this.globalFilterForm.controls['TillDate'].value)),
        //   'yyyy-MM-dd',
        // );
        const currDate = this.commonUtils.getFirstDayOfMonthByDate(dt);
        this.fetchHierarchyData(this.globalFilterForm.controls['SSG'].value, currDate, dt);
      }
    });
    this.globalFilterForm.get('Region')?.valueChanges.subscribe(val => {
      if (this.globalFilterForm.controls['Cluster'].value) {
        this.globalFilterForm.controls['Cluster'].setValue(undefined);
      }
      if (this.globalFilterForm.controls['City'].value) {
        this.globalFilterForm.controls['City'].setValue(undefined);
      }
      this.globalFilterForm.controls['Outlet'].setValue(undefined);
    });

    this.globalFilterForm.get('Cluster')?.valueChanges.subscribe(val => {
      if (this.globalFilterForm.controls['Cluster'].value) {
        this.globalFilterForm.controls['City'].setValue(undefined);
      }
      this.globalFilterForm.controls['Outlet'].setValue(undefined);
    });

    this.globalFilterForm.get('City')?.valueChanges.subscribe(val => {
      if (this.globalFilterForm.controls['City'].value) {
        this.globalFilterForm.controls['Cluster'].setValue(undefined);
        this.globalFilterForm.controls['SubRegion'].setValue(undefined);
      }
      this.globalFilterForm.controls['Outlet'].setValue(undefined);
    });
  }

  onApplyBtnClick() {
    this.filterChanged.emit();
    this.onApplyFilters();
    this.visible = false;
  }

  onApplyFilters() {
    // console.log(this.getFilterDataObject())
    // console.log(this.getFilterDataObject())
    this.globalFilterService.setData(this.getFilterDataObject());
    this.globalFilterService.fireChangeObservable();
  }

  arrToString(obj) {
    return obj ? obj.toString() : obj;
  }

  getObject(data, type) {
    let _output: any = [];
    for (var i = 0; i < data?.length; i++) {
      if (type === 'RegionName') {
        _output.push(data[i]['RegionName']);
      }
      if (type === 'SubRegionName') {
        _output.push(data[i]['SubRegionName']);
      }
      if (type === 'ClusterName') {
        _output.push(data[i]['ClusterName']);
      }
      if (type === 'CityName') {
        _output.push(data[i]['CityName']);
      }
      if (type === 'OutletCode') {
        _output.push(data[i]['OutletCode']);
      }
    }
    return _output;
  }

  getFilterDataObject() {
    return {
      region: this.arrToString(this.getObject(this.globalFilterForm.controls['Region'].value, 'RegionName')),
      subRegion: this.arrToString(this.getObject(this.globalFilterForm.controls['SubRegion'].value, 'SubRegionName')),
      cluster: this.arrToString(this.getObject(this.globalFilterForm.controls['Cluster'].value, 'ClusterName')),
      city: this.arrToString(this.getObject(this.globalFilterForm.controls['City'].value, 'CityName')),
      outlet: new GetOutletArrPipe().transform(
        this.hierarchyData.Outlet,
        this.getObject(this.globalFilterForm.controls['Region'].value, 'RegionName'),
        this.getObject(this.globalFilterForm.controls['SubRegion'].value, 'SubRegionName'),
        this.getObject(this.globalFilterForm.controls['Cluster'].value, 'ClusterName'),
        this.getObject(this.globalFilterForm.controls['City'].value, 'CityName'),
        this.getObject(this.globalFilterForm.controls['Outlet'].value, 'OutletCode'),
      ),
      session: this.globalFilterForm.controls['Session'].value,
      toDate: this.globalFilterForm.controls['ToDate'].value ? (this.globalFilterForm.controls['ToDate'].value) : '',
      fromDate: this.globalFilterForm.controls['FromDate'].value ? (this.globalFilterForm.controls['FromDate'].value) : '',
      tillDate: this.globalFilterForm.controls['TillDate'].value ? (this.globalFilterForm.controls['TillDate'].value) : '',
      year: this.globalFilterForm.controls['Year'].value?.Value,
      month: this.globalFilterForm.controls['Month'].value?.Value,
      quarter: this.globalFilterForm.controls['Quarter'].value?.value,
      sType: this.globalFilterForm.controls['SType'].value?.value,
      rType: this.globalFilterForm.controls['RType'].value?.value,
      ssg: this.globalFilterForm.controls['SSG'].value?.value,
      // rangeOutputMap:this.rangeOutput
    };
  }

  fetchHierarchyData(ssg?, currDate?, currDate2?, typeOfUnit?, showActiveOnly?) {
    this.dataLoaded = false;
    this.fetchHeirarchySubscription = this.globalFilterService.fetchHeirarchyData(
      this.router.url,
      ssg,
      currDate,
      currDate2,
      typeOfUnit,
      showActiveOnly
    ).subscribe(
      data => {
        this.hierarchyData = data || {};
        this.hierarchyData.Region = this.hierarchyData.Region || [];
        this.hierarchyData.SubRegion = this.hierarchyData.SubRegion || [];
        this.hierarchyData.Cluster = this.hierarchyData.Cluster || [];
        this.hierarchyData.City = this.hierarchyData.City || [];
        this.hierarchyData.Outlet = this.hierarchyData.Outlet || [];

        this.globalFilterService.setHeirarchyData(this.hierarchyData);
        this.dataLoaded = true;

        if (this.globalFilterService.getInitialOutletValue()) {
          this.initializeOutlet();
        }
        if (this.hierarchyData.Outlet.length === 1) {
          this.setBMOutlet();
        }
      },
      error => {
        this.dataLoaded = false;
        this.hierarchyData = {
          Region: [],
          SubRegion: [],
          Cluster: [],
          City: [],
          Outlet: []
        };
      }
    );
  }

  initializeOutlet() {
    const outletCode: any = this.globalFilterService.getInitialOutletValue();

    const outletObj = this.hierarchyData.Outlet.find(
      o => o.OutletCode === outletCode
    );
    if (outletObj) {
      this.globalFilterForm.controls['Outlet'].setValue([outletObj]);
    }
    this.globalFilterService.getData().outlet = outletCode;
    this.globalFilterService.resetPageInitials();
  }

  setBMOutlet() {
    const outletObj = this.hierarchyData.Outlet[0];
    if (outletObj) {
      this.globalFilterForm.controls['Outlet'].setValue([outletObj]);
    }
    this.onApplyFilters();
  }

  onYearChange() {
    this.monthArr = undefined;
    if (
      this.globalFilterForm.controls['Year'].value ===
      new Date().getFullYear().toString()
    ) {
      this.monthArr = this.commonUtils.getMonthByLimit(this.monthDefaultVal);
    } else {
      this.monthArr = this.commonUtils.getMonthByLimit(12);
    }
    const currDate = this.commonUtils.getFirstDayOfMonth(
      this.globalFilterForm.controls['Year'].value?.Value, this.globalFilterForm.controls['Month'].value?.Value);
    const currDate2 = this.commonUtils.getLastDayOfMonth(
      this.globalFilterForm.controls['Year'].value?.Value, this.globalFilterForm.controls['Month'].value?.Value);
    this.fetchHierarchyData(this.globalFilterForm.controls['SSG'].value, currDate, currDate2);
  }

  onSSGFilterChange() {
    let currDate: any = '';
    let currDate2: any = '';
    if (this.additionalFields.yearPicker.visible && this.additionalFields.monthPicker.visible) {
      currDate = this.commonUtils.getFirstDayOfMonth(
        this.globalFilterForm.controls['Year'].value?.Value, this.globalFilterForm.controls['Month'].value?.Value);
      currDate2 = this.commonUtils.getLastDayOfMonth(
        this.globalFilterForm.controls['Year'].value?.Value, this.globalFilterForm.controls['Month'].value?.Value);
    } else if (this.additionalFields.toDatePicker.visible) {
      currDate = moment(new Date((this.globalFilterForm.controls['ToDate'].value))).format("yyyy-MM-DD");
      currDate2 = moment(new Date((this.globalFilterForm.controls['ToDate'].value))).format("yyyy-MM-DD");
      // currDate = this.datePipe.transform(
      //   this.globalFilterForm.controls['ToDate'].value,
      //   'yyyy-MM-dd',
      // );
      // currDate2 = this.datePipe.transform(
      //   this.globalFilterForm.controls['ToDate'].value,
      //   'yyyy-MM-dd',
      // );
    }
    this.fetchHierarchyData(this.globalFilterForm.controls['SSG'].value, currDate, currDate2);
  }

  resetFilterFormData() {
    this.globalFilterForm.controls['Region'].setValue(undefined);
    this.globalFilterForm.controls['SubRegion'].setValue(undefined);
    this.globalFilterForm.controls['Cluster'].setValue(undefined);
    this.globalFilterForm.controls['City'].setValue(undefined);
    this.globalFilterForm.controls['Outlet'].setValue(undefined);
    this.globalFilterForm.controls['Region'].setErrors(null);
    this.globalFilterForm.controls['SubRegion'].setErrors(null);
    this.globalFilterForm.controls['Cluster'].setErrors(null);
    this.globalFilterForm.controls['City'].setErrors(null);
    this.globalFilterForm.controls['Region'].setErrors(null);
  }
  setValue(data) {
    const rangeEle = Object.keys(data);
    this.rangeOutput[rangeEle[0]] = data[rangeEle[0]];
  }

  executeHierarchyWithoutAdditionalFilters() {

    if (this.router.isActive('app/audit/my-workways', false) || this.router.isActive('app/masters/scheduler', false) || this.router.isActive('app/spinta/live-orders', false) || this.router.isActive('app/service-charge/employee-rating', false)) {
      this.fetchHierarchyData();
    }
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
    this.fetchHeirarchySubscription?.unsubscribe();
    this.additionalControlSubscription?.unsubscribe();
  }
}
